/**
 * Copy and per-reviewer context for the <Testimonials> reviews section
 * (homepage, /clutch, and any CMS page with a `testimonialsSection`).
 *
 * The review TEXT is never stored here: it comes verbatim from Sanity or from
 * `testimonial-feed.ts` / `clutch-testimonials.ts`. This module only adds
 * the context around a quote:
 *
 *  - `highlights`: the line to emphasise inside the quote. Each one must be a
 *    verbatim substring of that reviewer's text (pinned by
 *    `testimonials-section.test.ts`). Several candidates are allowed because
 *    the same person is quoted with different text on different surfaces
 *    (homepage pull-quote vs the fuller /clutch summary); the first one found
 *    in the text wins, and if none is found the quote renders unhighlighted.
 *  - `project`: what the engagement was. Taken from the review platform's
 *    own project/job title (see the comments beside each review in
 *    `testimonial-feed.ts`) or from the review's own words. Left out where
 *    neither says it.
 *  - `caseStudy`: only where a published /portfolio page exists for that
 *    client.
 *
 * No ratings per review: Clutch and Upwork publish the aggregate (see
 * `company-stats.ts`), and a star row on every card would imply a per-review
 * score we have not checked.
 */

import { REVIEWERS } from '@/content/reviewers';

export interface ReviewContext {
  readonly highlights?: readonly string[];
  readonly project?: string;
  readonly caseStudy?: { readonly href: string; readonly label: string };
}

export const REVIEW_CONTEXT: Readonly<Record<string, ReviewContext>> = {
  [REVIEWERS.imradSyed.clientName]: {
    highlights: ['EVDEV’s work helped us double our sales'],
    // Clutch: "E-Commerce Site Dev & Digital Mktg for Luxury Perfume House"
    project: 'Shopify store and digital marketing for a luxury perfume house',
    caseStudy: { href: '/portfolio/jo-milano', label: 'Jo Milano Paris case study' },
  },
  [REVIEWERS.umen.clientName]: {
    highlights: ['launched the new store on schedule and provided great post-support'],
    project: 'New store launch with post-launch support',
  },
  [REVIEWERS.maxim.clientName]: {
    highlights: ['a serious, reliable Shopify expert'],
    // Upwork: "Shopify Developer to Build Multi-Step Product Customizer"
    project: 'Multi-step product customizer on Shopify',
  },
  [REVIEWERS.jason.clientName]: {
    highlights: ['Deep knowledge of Shopify development (Liquid, JS, CSS)'],
    project: 'Shopify custom-theme development',
  },
  [REVIEWERS.aarielle.clientName]: {
    highlights: ['EVDEV is truly a rare find on this platform'],
    // Upwork: "Ongoing website edits/development", 20-month retainer
    project: 'Ongoing website development, 20-month retainer',
  },
  [REVIEWERS.mit.clientName]: {
    highlights: ['Super competent development and great communication'],
    // Upwork: "Web Developer For E-Commerce Site | Agency Work"
    project: 'Ecommerce site build for an agency',
  },
  [REVIEWERS.yrsa.clientName]: {
    highlights: ['made sure my store had some really awesome finishing touches'],
    // Upwork: "the utter shopify store for Soft Interrogations"
    project: 'Shopify store build',
  },
  [REVIEWERS.adam.clientName]: {
    highlights: ['Already planning on using EVDEV for another project'],
    // Upwork: "Convert Tailwind HTML / CSS site to Next.JS / Storefront API"
    project: 'Tailwind site rebuilt on Next.js and the Storefront API',
  },
  [REVIEWERS.tymkiv.clientName]: {
    highlights: ['40% increase in registrations', 'meticulous attention to detail'],
  },
  [REVIEWERS.batalova.clientName]: {
    highlights: ['a notable influx of visitors', 'clear communication'],
  },
  [REVIEWERS.kukhar.clientName]: {
    highlights: ['number of customers increased by 270%'],
  },
  [REVIEWERS.buryanova.clientName]: {
    highlights: ['boosting their online presence and user interaction'],
  },
  [REVIEWERS.lipatnikov.clientName]: {
    highlights: ['a 50% increase in sales, a 70% growth in organic traffic'],
  },
  [REVIEWERS.urtes.clientName]: {
    highlights: ['a functional and aesthetically pleasing website'],
  },
};

/** The first highlight candidate that appears verbatim in `text`, if any. */
export function findHighlight(text: string, context: ReviewContext | undefined): string | null {
  for (const candidate of context?.highlights ?? []) {
    if (candidate && text.includes(candidate)) return candidate;
  }
  return null;
}

/**
 * Split a quote around its highlight so the component can wrap the middle
 * part. Returns the whole text as `before` when there is nothing to highlight.
 */
export function splitOnHighlight(
  text: string,
  highlight: string | null,
): { before: string; match: string; after: string } {
  if (!highlight) return { before: text, match: '', after: '' };
  const at = text.indexOf(highlight);
  if (at === -1) return { before: text, match: '', after: '' };
  return {
    before: text.slice(0, at),
    match: highlight,
    after: text.slice(at + highlight.length),
  };
}

/** Quotes longer than this get clamped with a "Read full review" toggle. */
export const REVIEW_CLAMP_CHARS = 300;

export const TESTIMONIALS_COPY = {
  eyebrow: 'Client reviews',
  /** `platforms` is the review sources actually shown, e.g. "Clutch or Upwork". */
  intro: (platforms: string): string =>
    `What clients say about working with EVDEV. Every quote below is published on ${platforms}, so you can check it at the source.`,
  ratings: {
    /** Clutch's published aggregate; the count sits in CLUTCH_RATING_LABEL. */
    clutchScore: '5.0',
    clutchStarsAlt: '5 out of 5 stars on Clutch',
    clutchCta: 'Read the reviews',
    upworkCta: 'See the profile',
  },
  nav: {
    label: 'Review navigation',
    prev: 'Previous reviews',
    next: 'Next reviews',
    /** e.g. "1–3 of 10" */
    range: (first: number, last: number, total: number): string =>
      first === last ? `${first} of ${total}` : `${first}–${last} of ${total}`,
  },
  card: {
    projectLabel: 'Project',
    /** Accessible name of each card, e.g. "Review 2 of 10: Maxim". */
    label: (index: number, total: number, name: string): string =>
      `Review ${index} of ${total}: ${name}`,
    readMore: 'Read full review',
    readLess: 'Show less',
    sourceLinkLabel: (platform: string): string => `View this review on ${platform}`,
    clutchProfileLinkLabel: 'View EVDEV’s verified reviews on Clutch',
  },
  cta: {
    heading: 'Want this team on your store?',
    sub: 'Tell us what you need and get a fixed price before any work starts.',
    quote: { label: 'Get a fixed-price quote', href: '/#contact' },
    allReviews: { label: 'Read all reviews on Clutch' },
    portfolio: { label: 'See the portfolio', href: '/portfolio' },
  },
  platformNames: { clutch: 'Clutch', upwork: 'Upwork' },
} as const;
